/**
 * WebSocket hook for connecting to the real-time backend
 */

'use client';

import { useEffect, useCallback, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { WSClient } from '@/lib/ws-client';
import { api } from '@/lib/api';
import { NOTIFICATION_CENTER_EXCLUDES_CHAT } from '@/lib/notification-policy';
import { getNotificationLocationIdsForApi } from '@/lib/notification-location-filter';
import { asString, normalizeChatMessage, normalizeChatMessagePatch } from '@/lib/chat-normalizers';
import {
  asChatMessageList,
  asChatRoomList,
  chatMessageIdEqual,
  useAuthStore,
  useChatStore,
  useNotificationStore,
} from '@/lib/store';
import { PWA_DASHBOARD_HOME_INVALIDATE_EVENT } from '@/lib/app-dashboard-events';
import type { SessionProbeResult } from '@/lib/api';
import { getStoredAccessToken } from '@/lib/storage';
import { isCookieOnlyAuthClient } from '@/lib/session-health';
import type { SessionState } from '@/lib/runtime/types';
let globalWS: WSClient | null = null;
let globalHandlersRegistered = false;
const connectionSubscribers = new Set<(connected: boolean) => void>();
const typingTimeouts = new Map<string, number>();

let notificationCountRefetchTimer: number | null = null;
const NOTIFICATION_COUNT_REFETCH_DEBOUNCE_MS = 400;

function scheduleNotificationCountRefetch() {
  if (typeof window === 'undefined') return;
  if (notificationCountRefetchTimer !== null) {
    window.clearTimeout(notificationCountRefetchTimer);
  }
  notificationCountRefetchTimer = window.setTimeout(() => {
    notificationCountRefetchTimer = null;
    const locationIds = getNotificationLocationIdsForApi();
    void api
      .getNotificationCount({
        excludeChat: NOTIFICATION_CENTER_EXCLUDES_CHAT,
        ...(locationIds?.length ? { locationIds } : {}),
      })
      .then((r) => useNotificationStore.getState().setUnreadCount(r.count))
      .catch(() => {});
  }, NOTIFICATION_COUNT_REFETCH_DEBOUNCE_MS);
}

/** Native background / inactive: pause heartbeat to save battery. */
let appHeartbeatAllowed = true;

export function setAppHeartbeatAllowed(allowed: boolean) {
  appHeartbeatAllowed = allowed;
}

/**
 * Reconnect WS after runtime.session.probe — no second ensureSession round-trip.
 * Used by foreground-sync.ts on native resume.
 */
export function reconnectWebSocketAfterProbe(
  probeResult: SessionProbeResult | 'timeout',
  sessionState?: SessionState,
): void {
  const { isAuthenticated, user } = useAuthStore.getState();
  if (!isAuthenticated || !user) return;
  if (probeResult === 'expired' || sessionState === 'expired') return;

  const webCookieClient = isCookieOnlyAuthClient();
  const storedToken = webCookieClient ? '' : getStoredAccessToken() || '';
  if (!webCookieClient && !storedToken && probeResult !== 'ok') return;

  const ws = ensureWSClient(storedToken);
  registerGlobalHandlers();
  ws.reconnect(storedToken);
}

/** @deprecated Prefer foregroundSync() — avoids duplicate ensureSession after probe. */
export function reconnectWebSocketIfNeeded() {
  const { isAuthenticated, user } = useAuthStore.getState();
  if (!isAuthenticated || !user) return;
  void (async () => {
    const fresh = await api.ensureSession(false);
    if (!fresh) return;
    const wsToken = fresh === '__cookie_session__' ? '' : fresh;
    const ws = ensureWSClient(wsToken);
    registerGlobalHandlers();
    ws.reconnect(wsToken);
  })();
}

function notifyConnectionSubscribers(connected: boolean) {
  connectionSubscribers.forEach((subscriber) => subscriber(connected));
}

function ensureWSClient(token: string) {
  if (globalWS) {
    return globalWS;
  }

  const wsUrl = Capacitor.isNativePlatform()
    ? (process.env.NEXT_PUBLIC_API_URL || 'https://api.pick-pvz.ru')
    : (process.env.NEXT_PUBLIC_API_URL || window.location.origin);

  globalWS = new WSClient({
    url: wsUrl,
    token,
    onConnect: () => {
      console.log('WebSocket connected');
      notifyConnectionSubscribers(true);
    },
    onDisconnect: () => {
      console.log('WebSocket disconnected');
      notifyConnectionSubscribers(false);
    },
  });

  return globalWS;
}

function registerGlobalHandlers() {
  if (!globalWS || globalHandlersRegistered) {
    return;
  }

  globalHandlersRegistered = true;

  globalWS.on('message.new', (data) => {
    const normalized = normalizeChatMessage(data, {
      source: 'ws.message.new',
    });
    if (!normalized) return;

    const currentUserId = useAuthStore.getState().user?.id;
    const chatState = useChatStore.getState();
    const rid = Number(normalized.room_id);
    if (!Number.isFinite(rid) || rid < 1) return;

    const roomsList = asChatRoomList(chatState.rooms);
    const room = roomsList.find((item) => item.id === rid);
    const isOwnMessage = String(normalized.sender_user_id) === String(currentUserId);
    // WS JSON даёт room_id строкой; activeRoomId — число. Строгое !== давало ложный increment в открытой комнате.
    const active = chatState.activeRoomId;
    const inOpenRoom = active != null && Number(active) === rid;
    const msgTopic = normalized.topic_id != null ? Number(normalized.topic_id) : null;
    const activeForumTopic = chatState.activeForumTopicId;
    const viewingSameForumTopic =
      Boolean(room?.forum_enabled) &&
      inOpenRoom &&
      msgTopic != null &&
      activeForumTopic != null &&
      msgTopic === activeForumTopic;
    const isSystem = normalized.message_type === 'system';
    const incrementUnread =
      !isSystem &&
      !isOwnMessage &&
      (!inOpenRoom || (Boolean(room?.forum_enabled) && !viewingSameForumTopic));

    if (isOwnMessage) {
      chatState.removePendingFromSender(rid, String(normalized.sender_user_id ?? ''));
    }

    const isSupportFeed =
      room?.entity_type === 'support' ||
      room?.entity_type === 'support_case' ||
      (typeof room?.name === 'string' && room.name.toLowerCase().includes('поддержка'));
    const isChannelThreadReply =
      room?.type === 'channel' && normalized.reply_to_id != null && !isSupportFeed;

    if (isChannelThreadReply) {
      if (incrementUnread && !isOwnMessage) {
        chatState.incrementUnread(rid);
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('chat:channel-thread-message', {
            detail: { ...normalized, room_id: rid },
          })
        );
      }
    } else {
      chatState.addMessage(rid, normalized);
      const hadRoomInList = roomsList.some((item) => item.id === rid);
      chatState.touchRoom(
        Number(normalized.room_id),
        {
          content:
            normalized.content ||
            (asString(normalized.message_type) ? `[${asString(normalized.message_type)}]` : '[Сообщение]'),
          created_at: asString(normalized.created_at) || new Date().toISOString(),
          sender_name: asString(normalized.sender_name),
          message_type: asString(normalized.message_type),
          id: normalized.id,
          sender_user_id: normalized.sender_user_id ?? null,
          is_read: isOwnMessage ? (normalized.is_read ?? false) : undefined,
        },
        {
          incrementUnread,
          avatarUrl:
            room?.type === 'direct' && !isOwnMessage
              ? (typeof normalized.sender_avatar_url === 'string' ? normalized.sender_avatar_url : null)
              : undefined,
        }
      );
      if (!hadRoomInList) {
        void api
          .getChatRooms(true)
          .then((list) => useChatStore.getState().mergeRooms(Array.isArray(list) ? list : []))
          .catch(() => {});
      }
    }
  });

  globalWS.on('message.edit', (data) => {
    const normalizedPatch = normalizeChatMessagePatch(data, 'ws.message.edit');
    if (!normalizedPatch) return;
    const { id, patch } = normalizedPatch;

    const rooms = useChatStore.getState().messages;
    for (const [roomId, raw] of Object.entries(rooms)) {
      const msgs = asChatMessageList(raw);
      if (msgs.some((message) => chatMessageIdEqual(message.id, id))) {
        useChatStore.getState().updateMessage(Number(roomId), id, patch);
        break;
      }
    }
  });

  globalWS.on('message.delete', (data) => {
    const normalizedPatch = normalizeChatMessagePatch(data, 'ws.message.delete');
    if (!normalizedPatch) return;

    const { id, patch } = normalizedPatch;
    const deletedAtRaw = patch.deleted_at;

    const deletedAt =
      typeof deletedAtRaw === 'string' && deletedAtRaw.trim()
        ? deletedAtRaw
        : new Date().toISOString();

    const rooms = useChatStore.getState().messages;
    for (const [roomId, raw] of Object.entries(rooms)) {
      const msgs = asChatMessageList(raw);
      if (msgs.some((message) => chatMessageIdEqual(message.id, id))) {
        useChatStore.getState().updateMessage(Number(roomId), id, {
          deleted_at: deletedAt,
          content: '',
          attachments: [],
          reactions: [],
        });
        break;
      }
    }
  });

  globalWS.on('typing.start', (data) => {
    const currentUserId = useAuthStore.getState().user?.id;
    if (data.user_id === currentUserId) return;

    const roomId = Number(data.room_id || useChatStore.getState().activeRoomId || 0);
    if (!roomId) return;

    const timeoutKey = `${roomId}:${data.user_id}`;
    const existingTimeout = typingTimeouts.get(timeoutKey);
    if (existingTimeout) {
      window.clearTimeout(existingTimeout);
    }

    useChatStore.getState().setTyping(roomId, data.user_id, data.user_name, true);
    const timeoutId = window.setTimeout(() => {
      useChatStore.getState().setTyping(roomId, data.user_id, data.user_name, false);
      typingTimeouts.delete(timeoutKey);
    }, 5000);
    typingTimeouts.set(timeoutKey, timeoutId);
  });

  globalWS.on('typing.stop', (data) => {
    const currentUserId = useAuthStore.getState().user?.id;
    if (data.user_id === currentUserId) return;

    const roomId = Number(data.room_id || useChatStore.getState().activeRoomId || 0);
    if (!roomId) return;

    const timeoutKey = `${roomId}:${data.user_id}`;
    const existingTimeout = typingTimeouts.get(timeoutKey);
    if (existingTimeout) {
      window.clearTimeout(existingTimeout);
      typingTimeouts.delete(timeoutKey);
    }

    useChatStore.getState().setTyping(roomId, data.user_id, data.user_name, false);
  });

  globalWS.on('notification.new', (data) => {
    const t = data?.type;
    if (t === 'chat_message' || t === 'chat_mention' || t === 'chat_group_message') {
      return;
    }
    useNotificationStore.getState().addNotification(data);
    scheduleNotificationCountRefetch();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(PWA_DASHBOARD_HOME_INVALIDATE_EVENT));
    }
  });

  globalWS.on('notification.count', (_data: { count?: unknown; action?: string }) => {
    // Серверный count без фильтра по точкам — всегда пересчитываем с теми же location_ids, что и лента.
    scheduleNotificationCountRefetch();
  });

  globalWS.on('messages.read', (data) => {
    const { room_id, user_id, last_message_id } = data;
    const currentUserId = useAuthStore.getState().user?.id;
    if (!room_id || !last_message_id || String(user_id) === String(currentUserId)) return;

    const rooms = useChatStore.getState().messages;
    const rmid = Number(room_id);
    const msgs = asChatMessageList(rooms[rmid]);
    for (const message of msgs) {
      if (String(message.sender_user_id) === String(currentUserId) && message.id <= last_message_id && !message._pending) {
        useChatStore.getState().updateMessage(rmid, message.id, { is_read: true });
      }
    }

    const lid = Number(last_message_id);
    if (!Number.isFinite(lid) || currentUserId == null) return;
    useChatStore.setState((state) => ({
      rooms: asChatRoomList(state.rooms).map((r) => {
        if (Number(r.id) !== rmid) return r;
        const lm = r.last_message;
        if (!lm || lm.id == null || String(lm.sender_user_id) !== String(currentUserId)) return r;
        if (Number(lm.id) <= lid) {
          return { ...r, last_message: { ...lm, is_read: true } };
        }
        return r;
      }),
    }));
  });

  globalWS.on('role.changed', (data) => {
    const newRole = data?.new_role;
    if (!newRole) return;
    void api.getBootstrap().then((bootstrap) => {
      if (bootstrap) {
        const { setBootstrap } = useAuthStore.getState();
        setBootstrap(bootstrap);
      }
    }).catch(() => {});
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('app:role-changed', { detail: data }));
    }
  });
}

export function useWebSocket() {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [isConnected, setIsConnected] = useState(() => globalWS?.isConnected ?? false);

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    let cancelled = false;
    let unsubscribeAuthEvents: (() => void) | undefined;

    void (async () => {
      const token = await api.ensureSession(false);
      if (cancelled || !token) return;

      // WSClient сам получит ticket для web или использует token для native
      const wsToken = token === '__cookie_session__' ? '' : token;
      const ws = ensureWSClient(wsToken);
      registerGlobalHandlers();
      connectionSubscribers.add(setIsConnected);
      setIsConnected(ws.isConnected);

      // connect() теперь асинхронный и сам запрашивает ticket при необходимости
      await ws.connect(wsToken || undefined);

      unsubscribeAuthEvents = api.subscribeAuthEvents((event) => {
        if (event.type === 'refreshed') {
          ws.reconnect(event.accessToken);
          return;
        }

        ws.disconnect();
        useChatStore.getState().clearTyping();
        notifyConnectionSubscribers(false);
      });
    })();

    return () => {
      cancelled = true;
      connectionSubscribers.delete(setIsConnected);
      unsubscribeAuthEvents?.();
    };
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    if (!isAuthenticated || !isConnected) return;

    const heartbeatInterval = window.setInterval(() => {
      if (!appHeartbeatAllowed) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      globalWS?.send('heartbeat');
    }, 25000);

    return () => {
      window.clearInterval(heartbeatInterval);
    };
  }, [isAuthenticated, isConnected]);

  // T-509: немедленный reconnect при возврате фокуса вкладки / восстановлении сети
  useEffect(() => {
    if (!isAuthenticated) return;

    const onVisible = () => {
      if (document.visibilityState === 'visible' && globalWS && !globalWS.isConnected) {
        globalWS.reconnect();
      }
    };
    const onOnline = () => {
      if (globalWS && !globalWS.isConnected) {
        globalWS.reconnect();
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, [isAuthenticated]);

  const sendMessage = useCallback((event: string, data: any = {}, roomId?: number): boolean => {
    return globalWS?.send(event, data, roomId) ?? false;
  }, []);

  const sendTyping = useCallback((roomId: number, isTyping: boolean) => {
    globalWS?.send(isTyping ? 'typing.start' : 'typing.stop', {}, roomId);
  }, []);

  const markRead = useCallback((roomId: number, lastMessageId: number, topicId?: number) => {
    const payload: { last_message_id: number; topic_id?: number } = { last_message_id: lastMessageId };
    if (topicId != null) payload.topic_id = topicId;
    globalWS?.send('messages.read', payload, roomId);
  }, []);

  return {
    sendMessage,
    sendTyping,
    markRead,
    isConnected,
  };
}
